import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Paper,
  Alert
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import ReleaseNotesService, { ReleaseNote } from '../services/releaseNotesService';
import LoadingState from './LoadingState';

interface ReleaseNotesListProps {
  maxItems?: number;
  defaultExpanded?: boolean;
  showTitle?: boolean;
  includeHidden?: boolean;
}

const ReleaseNotesList: React.FC<ReleaseNotesListProps> = ({
  maxItems,
  defaultExpanded = false,
  showTitle = true,
  includeHidden = false
}) => {
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReleaseNotes = async () => {
      try {
        setLoading(true);
        // Get only visible notes for public view, or all notes for admin
        const notes = includeHidden 
          ? await ReleaseNotesService.getAll(true)
          : await ReleaseNotesService.getPublic();
        
        // Sort by release date, newest first
        const sortedNotes = [...notes].sort((a, b) => {
          return new Date(b.release_date).getTime() - new Date(a.release_date).getTime();
        });
        
        // Apply maxItems limit if specified
        const limitedNotes = maxItems ? sortedNotes.slice(0, maxItems) : sortedNotes;
        setReleaseNotes(limitedNotes);
        setError(null);
      } catch (err) {
        console.error('Error fetching release notes:', err);
        setError('Unable to load release notes. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchReleaseNotes();
  }, [maxItems, includeHidden]);

  // Format date for display
  const formatReleaseDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMMM d, yyyy');
    } catch (e) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <LoadingState message="Loading release notes..." variant="page" size="medium" />
    );
  }

  if (error) {
    return (
      <Alert severity="info" className="my-4">No release notes could be loaded. Please try again later.</Alert>
    );
  }

  if (releaseNotes.length === 0) {
    return (
      <Paper className="p-6">
        <Typography>No release notes are available at this time. Check back later for updates.</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {showTitle && (
        <Typography variant="h4" className="mb-4">Release Notes</Typography>
      )}
      
      {releaseNotes.map((note, index) => (
        <Accordion 
          key={note.id} 
          defaultExpanded={defaultExpanded || index === 0}
          className="mb-2"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="flex flex-col sm:flex-row sm:items-center w-full pr-4">
              <Typography variant="h6" className="flex-grow">
                {note.title}
              </Typography>
              <Box className="flex gap-2 mt-2 sm:mt-0">
                {note.version && (
                  <Chip 
                    label={`v${note.version}`}
                    color="primary"
                    size="small"
                    className="font-mono"
                  />
                )}
                <Chip 
                  label={formatReleaseDate(note.release_date)}
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Divider className="mb-4" />
            <Box className="prose prose-sm max-w-none">
              <ReactMarkdown>
                {note.description}
              </ReactMarkdown>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default ReleaseNotesList; 